<template>
  <div>
    <es-loader
      v-if="loading || loadingSavedData"
      style="margin-top: 3rem"
    />
    <template v-else>
      <es-form>
        <template #es-form-header style="margin: 0">
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'search']" size="lg" />
            </div>
            <div class="header-elements">
              <h6 class="title is-6">
                Search Settings
              </h6>
            </div>
          </div>
        </template>
        <template #es-form>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Person Type
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div>
                      <es-select
                        v-bind:multi="true"
                        v-on:update:values="personTypes = $event"
                        ref="resetPersonTypes"
                        valueTpl="${name}"
                        :preSelected="asArray(personTypes)"
                        :setValues="sortedPersonTypesOptions"
                        keyValue="code"
                        v-bind:clearOut="true"
                        :hasError="hasErrorPersonType"
                        :errorMsg="msgPersonType"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Search In
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="buttons has-addons">
                      <button
                        v-for="(option, index) in searchInOptions"
                        :key="index"
                        class="button button--form"
                        :class="{ 'is-active': selectedSearchInOption === option.label }"
                        @click="checkSearchInOption(option.label)"
                        style="min-width: 100px"
                      >
                        {{ option.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Match Method
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    SQL
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
      <es-form>
        <template #es-form-header style="margin: 0">
          <div class="header-container">
            <div
              class="header-elements"
              style="margin-left: 3rem;"
            >
              <h6 class="title is-6">
                Search Parameters
              </h6>
            </div>
          </div>
        </template>
        <template #es-form>
          <div
            v-if="rows.length > 0"
            class="table-wrapper"
          >
            <table class="table is-fullwidth">
              <colgroup>
                <col>
                <col>
                <col style="width: 20%">
                <col style="width: 20%">
                <col style="width: 35%">
                <col style="width: 1%">
              </colgroup>
              <thead>
                <tr>
                  <th>Row Number</th>
                  <th>Operator</th>
                  <th>Field Name</th>
                  <th>Comparison Operator</th>
                  <th>Search Term</th>
                  <th>
                    <span class="sr-only">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in rows"
                  :key="index"
                >
                  <td style="text-align: center;">
                    <div class="control">
                      {{ index + 1 }}
                    </div>
                  </td>
                  <td>
                    <div class="control">
                      <es-select
                        v-if="index > 0"
                        v-on:update:values="updateAndValidateField(index, $event, 'operator')"
                        valueTpl="${label}"
                        :setValues="operators"
                        :preSelected="asArray(row.operator)"
                        keyValue="value"
                        :hasError="row.hasErrorOperator"
                        :errorMsg="row.msgOperator"
                      />
                      <span v-if="index === 0">
                        (
                      </span>
                      <span v-else-if="index === rows.length - 1 && rows.length > 1">
                        )
                      </span>
                    </div>
                  </td>
                  <td class="editable-cell">
                    <div class="control">
                      <es-select
                        v-on:update:values="updateAndValidateField(index, $event, 'fieldName')"
                        valueTpl="${label}"
                        :setValues="fieldNames"
                        :preSelected="asArray(row.fieldName)"
                        keyValue="value"
                        v-bind:clearOut="true"
                        :hasError="row.hasErrorFieldName"
                        :errorMsg="row.msgFieldName"
                        ref="resetFieldName"
                      />
                    </div>
                  </td>
                  <td class="editable-cell">
                    <div class="control">
                      <es-select
                        v-on:update:values="updateAndValidateField(
                          index, $event, 'comparisonOperator'
                        )"
                        valueTpl="${label}"
                        :setValues="comparisonOperators"
                        :preSelected="asArray(row.comparisonOperator)"
                        keyValue="value"
                        v-bind:clearOut="true"
                        :hasError="row.hasErrorComparisonOperator"
                        :errorMsg="row.msgComparisonOperator"
                      />
                    </div>
                  </td>
                  <td class="editable-cell">
                    <div class="control">
                      <template
                        v-if="row.fieldName && row.fieldName[0]
                          && row.fieldName[0].value === 'GENDER_CODE'"
                      >
                        <es-select
                          v-on:update:values="updateAndValidateField(index, $event, 'searchTerm')"
                          keyValue="code"
                          valueTpl="${description}"
                          :setValues="activeGenders"
                          v-model="rows[index].searchTerm"
                          ref="resetSearchTerm"
                          v-bind:clearOut="true"
                        />
                      </template>
                      <template
                        v-else-if="row.fieldName && row.fieldName[0]
                          && row.fieldName[0].value === 'DISTRICT_CODE'"
                      >
                        <es-select
                          v-bind:search="true"
                          api="districts"
                          v-on:update:values="updateAndValidateField(index, $event, 'searchTerm')"
                          :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                          v-model="rows[index].searchTerm"
                          :apiQueryParams="getAllDistricts"
                          searchMinLength="2"
                          keyValue="code"
                          ref="resetSearchTerm"
                          v-bind:clearOut="true"
                        />
                      </template>
                      <template
                        v-else-if="row.fieldName && row.fieldName[0]
                          && row.fieldName[0].value === 'SCHOOL_CODE'"
                      >
                        <es-select
                          v-bind:search="true"
                          api="schools"
                          :apiQueryParams="getAllSchools"
                          v-on:update:values="updateAndValidateField(index, $event, 'searchTerm')"
                          :subValueTplFn="(result) =>
                          `${result.districtLocation.name} (<i>${result.districtCode}</i>)`"
                          :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                          searchMinLength="2"
                          keyValue="code"
                          ref="resetSearchTerm"
                          v-model="rows[index].searchTerm"
                          v-bind:clearOut="true"
                        />
                      </template>
                      <template
                        v-else-if="row.fieldName && row.fieldName[0]
                          && row.fieldName[0].value === 'SOURCE_CODE'"
                      >
                        <es-select
                          valueTpl="${description}"
                          :setValues="sources"
                          keyValue="code"
                          v-on:update:values="updateAndValidateField(index, $event, 'searchTerm')"
                          v-bind:clearOut="true"
                          ref="resetSearchTerm"
                          v-model="rows[index].searchTerm"
                        />
                      </template>
                      <template
                        v-else-if="row.fieldName && row.fieldName[0]
                        && (row.fieldName[0].value === 'cdf_9'
                        || row.fieldName[0].value === 'DATE_OF_BIRTH')"
                      >
                        <input
                          class="input calendar"
                          :class="{
                            'placeholder': !row.searchTerm,
                            'is-danger' : row.hasErrorSearchTerm
                          }"
                          type="date"
                          v-model="row.searchTerm"
                          @change.prevent="validateFields(row.searchTerm)"
                          aria-label="Search Term"
                        />
                      </template>
                      <template
                        v-else-if="row.fieldName && row.fieldName[0]
                        && (row.fieldName[0].value === 'DATE_OF_BIRTH_SEGMENTS')"
                      >
                        <div class="birthdate-container">
                          <div class="date-segment">
                            <input class="input date-input"
                              type="text"
                              :class="{'is-danger' : row.hasErrorBirthMonth}"
                              placeholder="Month"
                              v-model="row.birthMonth"
                              @input="validateDateFSegmentFields(row)"
                              aria-label="Birth Month"
                            />
                            <span
                              v-if="row.hasErrorBirthMonth"
                              class="help is-danger"
                            >
                              {{ row.msgBirthMonth }}
                            </span>
                          </div>
                          <span class="separator">
                            /
                          </span>
                          <div class="date-segment">
                            <input class="input date-input"
                              type="text"
                              :class="{'is-danger' : row.hasErrorBirthDay}"
                              placeholder="Day"
                              v-model="row.birthDay"
                              @input="validateDateFSegmentFields(row)"
                              aria-label="Birth Day"
                            />
                            <span
                              v-if="row.hasErrorBirthDay"
                              class="help is-danger"
                            >
                              {{ row.msgBirthDay }}
                            </span>
                          </div>
                          <span class="separator">
                            /
                          </span>
                          <div class="date-segment">
                            <input class="input date-input"
                              type="text"
                              :class="{'is-danger' : row.hasErrorBirthYear}"
                              placeholder="Year"
                              v-model="row.birthYear"
                              @input="validateDateFSegmentFields(row)"
                              aria-label="Birth Year"
                            />
                            <span
                              v-if="row.hasErrorBirthYear"
                              class="help is-danger"
                            >
                              {{ row.msgBirthYear }}
                            </span>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <input
                          class="input"
                          type="text"
                          :class="{'is-danger' : row.hasErrorSearchTerm}"
                          v-model="row.searchTerm"
                          @keyup.prevent="validateFields(row.searchTerm)"
                          aria-label="Search Term"
                        />
                      </template>
                      <span
                        v-if="row.hasErrorSearchTerm"
                        class="help is-danger"
                      >
                        {{ row.msgSearchTerm }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="control">
                      <a
                        href="#"
                        @click.prevent="deleteRow(index)"
                        title="Close"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'times']"
                          size="lg"
                        />
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="control"
            style="margin-top: 0rem;margin-left: 3rem;"
          >
            <button
              @click="addNewParameter"
              :class="{ 'disabled': disableAddParameter }"
              class="button button-link has-underline"
              :disabled="disableAddParameter"
            >
              + Add New Parameter {{ rows.length + 1 }}
            </button>
          </div>
        </template>
      </es-form>
      <es-form>
        <template #es-form-header style="margin: 0">
          <div class="header-container">
            <div
              class="header-elements"
              style="margin-left: 3rem;"
            >
              <h6 class="title is-6">
                Search For SQL:
              </h6>
              <a href="#" @click="toggleDisplay">
                View/Update Search Parameters SQL
              </a>
            </div>
          </div>
        </template>
        <template #es-form>
          <div
            class="field is-horizontal"
            style="margin-left: 2rem;"
          >
            <p
              v-if="showQuery"
              class="sql-query"
            >
              {{
                sqlQuery.length > 0 && sqlQuery!=="()"  ? sqlQuery: 'No Parameters have been added.'
              }}
            </p>
          </div>
        </template>
      </es-form>
      <div
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 5.15rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click="reset"
          style="margin-right: 1rem"
        >
          Reset
        </button>
        <button
          class="button is-primary"
          :disabled="loadingStudents || !this.rows.length"
          @click="search"
        >
          Search
        </button>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import {
  faUser,
  faSearch,
  faRemove,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faUser,
  faSearch,
  faRemove,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsLoader,
    EsSelect,
  },
  props: {
    loadingStudents: {
      type: Boolean,
    },
    page: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'lastUpdateDate',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
    userDistrictInfo: {
      type: Array,
    },
    userSchoolInfo: {
      type: Array,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      rows: [],
      loadingSavedData: false,
      restoringSavedData: false,
      displaySQL: false,
      sqlQuery: '',
      sqlQueryWithCodes: '',
      showQuery: false,
      allSubmissionSpecs: [],
      matchMethod: [],
      hasErrorPersonType: false,
      hasErrorSchoolYear: false,
      msgSchoolYear: '',
      districts: this.userDistrictInfo.length === 1 ? this.userDistrictInfo : [],
      schools: this.userSchoolInfo.length === 1 ? this.userSchoolInfo : [],
      msgPersonType: '',
      searchInOptions: [],
      selectedSearchInOption: 'Master',
      personType: null,
      personTypes: [],
      userPersonTypes: this.$store.state.user.userPersonTypes
        .map(item => item.personType).filter(a => a.status === true),
      operators: [
        { label: 'AND', value: 'AND' },
        { label: ') AND (', value: ') AND (' },
        { label: 'OR', value: 'OR' },
        { label: ') OR (', value: ') OR (' },
      ],
      comparisonOperators: [
        { label: 'contains', value: 'LIKE "%{value}%"' },
        { label: 'starts with', value: 'LIKE "{value}%"' },
        { label: 'ends with', value: 'LIKE "%"' },
        { label: 'is (exact)', value: '=' },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    };
  },
  created() {
    this.setDataElements();
  },
  async mounted() {
    this.loading = true;
    this.loadingError = false;
    try {
      await Promise.all([
        this.loadPersonTypes(),
        this.loadSources(),
        this.loadSources(),
      ]);
      this.personType = this.defaultPersonType;

      // Auto-execute search if searchByAdvanced state exists in Vuex
      const saved = this.$store.getters['personSearchModule/getSearchByType']('searchByAdvanced');
      if (saved && Object.keys(saved).length > 0) {
        this.loadingSavedData = true;
        this.$emit('show', false);
        this.$emit('loadingSavedData', true);
        this.restoringSavedData = true;

        this.selectedSearchInOption = saved.selectedSearchInOption || 'Master';
        this.personType = saved.personType || this.personType;
        this.personTypes = this.normalizePersonTypes(saved.personTypes);
        this.rows = this.normalizeRows(saved.rows);
        this.showQuery = !!saved.showQuery;

        await this.$nextTick();
        this.generateSQL();

        this.restoringSavedData = false;
        this.loadingSavedData = false;
        this.$emit('loadingSavedData', false);
        this.search({ saveState: false, pageNum: saved.pageNum || null });
      }
    } catch (err) {
      this.loadingError = true;
      this.messages.push('There were some errors. Please try again. If errors persist please contact support.');
      this.showNotification('danger', this.messages);
    } finally {
      if (this.loadingSavedData) {
        this.loadingSavedData = false;
        this.$emit('loadingSavedData', false);
      }
      this.loading = false;
    }
  },
  watch: {
    '$route.query.page': {
      handler(newPage) {
        const saved = this.$store.getters['personSearchModule/getSearchByType']('searchByAdvanced');
        if (saved && Object.keys(saved).length > 0) {
          this.$store.dispatch('personSearchModule/setSearchState', {
            searchType: 'searchByAdvanced',
            ...saved,
            pageNum: newPage ? Number(newPage) : null,
          });
        }
      },
    },
    rows: {
      deep: true,
      handler: 'generateSQL',
    },
    personTypes: {
      deep: true,
      handler() {
        const updatedFieldNames = this.fieldNames.map(field => field.value);
        this.rows = this.rows.filter(row => row.fieldName && row.fieldName[0]
          && updatedFieldNames.includes(row.fieldName[0].value));
      },
    },
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'sources',
      'pidSchoolYearsDesc',
      'activePersonTypes',
      'defaultPersonType',
      'activeGenders',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getEnabledFieldNamesForPersonType',
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
    ]),
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    fieldNames() {
      const codes = [
        'STATE_STUDENT_ID',
        'LAST_NAME',
        'FIRST_NAME',
        'MIDDLE_NAME',
        'SUFFIX',
        'GENDER_CODE',
        'DATE_OF_BIRTH(Full)',
        'DATE_OF_BIRTH(Segments)',
        'SOCIAL_SECURITY_NUMBER',
        'DISTRICT_CODE',
        'SCHOOL_CODE',
        'LOCAL_STUDENT_ID',
        'SCHOOL_YEAR',
        'PREVIOUS_LAST_NAME',
        'ALIAS_ID',
        'cdf_1',
        'cdf_2',
        'cdf_3',
        'cdf_4',
        'cdf_5',
        'cdf_6',
        'cdf_7',
        'cdf_8',
        'cdf_9',
        'cdf_10',
        'cdf_11',
        'SOURCE_CODE',
        'LAST_TRAN_SERIAL_NUMBER',
      ];
      const sortedCodes = codes.slice().sort((a, b) => a.localeCompare(b));
      const fieldNames = [];

      sortedCodes.forEach((code) => {
        let enabledField = null;
        let modifiedCode = code;

        if (code === 'DATE_OF_BIRTH(Full)' || code === 'DATE_OF_BIRTH(Segments)') {
          modifiedCode = 'DATE_OF_BIRTH';
        }

        // If only one person type selected,use label for that person type
        if (this.personTypes.length === 1) {
          const singleType = this.personTypes[0];
          const isEnabled = this.getFieldEnabledForPersonType(modifiedCode, 'SEARCH', singleType.code);
          if (isEnabled) {
            enabledField = this.getFieldLabelByPersonType(modifiedCode, 'SEARCH', singleType.code);
          }
        } else { // if multiple person types or none selected, use default label
          const personTypesToCheck = this.personTypes.length
            ? this.personTypes : this.activePersonTypes;
          personTypesToCheck.some((personType) => {
            const enabledFields = this.getEnabledFieldNamesForPersonType(modifiedCode, 'SEARCH', personType.code);
            if (enabledFields.length > 0) {
              [enabledField] = enabledFields;
              return true;
            }
            return false;
          });
        }

        if (code === 'ALIAS_ID') {
          enabledField = 'Alias ID';
        } else if (code === 'DATE_OF_BIRTH(Full)') {
          enabledField = enabledField ? `${enabledField} (Full)` : null;
        } else if (code === 'DATE_OF_BIRTH(Segments)') {
          enabledField = enabledField ? `${enabledField} (Segments)` : null;
          modifiedCode = 'DATE_OF_BIRTH_SEGMENTS';
        } else if (code === 'LAST_TRAN_SERIAL_NUMBER') {
          enabledField = 'Transaction ID';
        }
        fieldNames.push({ label: enabledField, value: modifiedCode });
      });

      const filteredFieldNames = fieldNames.filter(field => field.label !== null);

      filteredFieldNames.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      return filteredFieldNames;
    },
    getAllDistricts() {
      const params = {};
      params.allDistricts = true;
      return params;
    },
    getAllSchools() {
      const params = {};
      params.allSchools = true;
      return params;
    },
    getDistrictParams() {
      const params = {};
      if (this.districts.length > 0) {
        params.districtCode = this.districts.map(d => d.code);
        params.active = 'all';
      }
      return params;
    },
    disableAddParameter() {
      return this.rows.some(row => !row.fieldName && !row.comparisonOperator && !row.searchTerm);
    },
    filterUserAccessiblePersonTypes() {
      const userPersonTypesCodes = this.userPersonTypes.map(item => item.code);
      return this.activePersonTypes.filter(p => userPersonTypesCodes.includes(p.code));
    },
    sortedPersonTypesOptions() {
      return this.filterUserAccessiblePersonTypes.map(p => ({
        code: p.code,
        name: p.name,
      })).sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    allowedCharacters() {
      return this.properties['RULE-ALLOWED_CHARACTERS'];
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'SEARCH', this.personType === null ? this.defaultPersonType : this.personType);
    },
    matchMethodWidth() {
      const width = '100px';
      return `min-width: ${width}`;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadPersonTypes',
      'loadSources',
    ]),
    asArray(value) {
      return Array.isArray(value) ? value : [];
    },
    normalizePersonTypes(savedPersonTypes) {
      if (!Array.isArray(savedPersonTypes) || !savedPersonTypes.length) {
        return [];
      }

      return savedPersonTypes
        .map((p) => {
          const code = p && p.code ? p.code : p;
          if (!code) {
            return null;
          }
          return this.sortedPersonTypesOptions.find(opt => opt.code === code) || { code };
        })
        .filter(Boolean);
    },
    normalizeRows(savedRows) {
      if (!Array.isArray(savedRows) || !savedRows.length) {
        return [];
      }

      return savedRows.map((row) => {
        const normalized = { ...row };

        const fieldValue = row && row.fieldName && row.fieldName[0]
          ? row.fieldName[0].value
          : null;
        if (fieldValue) {
          const field = this.fieldNames.find(f => f.value === fieldValue);
          normalized.fieldName = field ? [field] : row.fieldName;
        }

        const compValue = row && row.comparisonOperator && row.comparisonOperator[0]
          ? row.comparisonOperator[0].value
          : null;
        if (compValue) {
          const comp = this.comparisonOperators.find(c => c.value === compValue);
          normalized.comparisonOperator = comp ? [comp] : row.comparisonOperator;
        }

        const opValue = row && row.operator && row.operator[0]
          ? row.operator[0].value
          : null;
        if (opValue) {
          const op = this.operators.find(o => o.value === opValue);
          normalized.operator = op ? [op] : row.operator;
        }

        return normalized;
      });
    },
    checkSearchInOption(selection) {
      this.selectedSearchInOption = selection;
    },
    deleteRow(index) {
      this.rows.splice(index, 1);
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'SEARCH', this.personType);
    },
    updateAndValidateField(index, selectedValue, fieldToUpdate) {
      const row = this.rows[index];
      if (fieldToUpdate === 'searchTerm') {
        if (Array.isArray(selectedValue)) {
          selectedValue.forEach((value, i) => {
            if (this.rows[index + i]) {
              const valueToSet = row.fieldName[0].value === 'SCHOOL_YEAR' ? value.year : value.code;
              this.$set(this.rows[index + i], fieldToUpdate, valueToSet);
            }
          });
        }
      } else {
        this.$set(row, fieldToUpdate, selectedValue);
        if (fieldToUpdate === 'operator' && !row.operator) {
          this.$set(row, 'operator', 'AND');
        }
        if (selectedValue !== null && fieldToUpdate !== 'searchTerm' && fieldToUpdate === 'fieldName') {
          this.resetSearchTerm(index);
          this.$set(row, 'birthDay', '');
          this.$set(row, 'birthMonth', '');
          this.$set(row, 'birthYear', '');
        }
      }
      this.validateFields();
    },
    resetSearchTerm(index) {
      const row = this.rows[index];
      if (row) {
        this.$set(row, 'searchTerm', '');
        if (this.$refs.resetSearchTerm && this.$refs.resetSearchTerm[index]) {
          this.$refs.resetSearchTerm[index].clear();
        }
        this.validateFields();
      }
    },
    validateFields() {
      try {
        let hasError = false;
        this.rows.forEach((row) => {
          this.checkRequiredFields(row);
          const fieldName = row.fieldName && row.fieldName[0] && row.fieldName[0].value;
          const fieldLabel = row.fieldName && row.fieldName[0] && row.fieldName[0].label;

          if (fieldName && row.searchTerm) {
            if (this.validateMaxLength(row, fieldName, fieldLabel)) {
              hasError = true;
            }
            if (this.validateNumericFields(row, fieldName, fieldLabel)) {
              hasError = true;
            }
            if (this.validateNameFields(row, fieldName, fieldLabel)) {
              hasError = true;
            }
            if (this.validateAlphanumericFields(row, fieldName, fieldLabel)) {
              hasError = true;
            }
            if (this.validateSSNField(row, fieldName, fieldLabel, fieldLabel)) {
              hasError = true;
            }
            if (this.validateSchoolYearField(row, fieldName, fieldLabel)) {
              hasError = true;
            }
            if (this.validateDateField(row, fieldName, fieldLabel)) {
              hasError = true;
            }
          }
          if (fieldName === 'DATE_OF_BIRTH_SEGMENTS') {
            if (this.validateDateFSegmentFields(row)) {
              hasError = true;
            }
          }

          if (!hasError) {
            hasError = row.hasErrorFieldName || row.hasErrorOperator
              || row.hasErrorComparisonOperator || row.hasErrorSearchTerm;
          }
        });

        if (!hasError) {
          this.resetNotifications();
        }
        return !hasError;
      } catch (error) {
        return false;
      }
    },
    validateMaxLength(row, fieldName, fieldLabel) {
      const lookupFieldName = fieldName === 'ALIAS_ID' ? 'ALT_STUDENT_ID' : fieldName;

      let maxLength = this.fieldLength(lookupFieldName);

      if (!maxLength || maxLength === 0) {
        const fallbackLengths = {
          FIRST_NAME: 150,
          LAST_NAME: 150,
          MIDDLE_NAME: 150,
          PREVIOUS_LAST_NAME: 150,
          SOCIAL_SECURITY_NUMBER: 11,
          SUFFIX: 150,
          LOCAL_STUDENT_ID: 60,
          STATE_STUDENT_ID: 20,
          SCHOOL_YEAR: 4,
          ALT_STUDENT_ID: 75,
          ALIAS_ID: 75,
          cdf_1: 60,
          cdf_2: 60,
          cdf_3: 60,
          cdf_4: 60,
          cdf_5: 60,
          cdf_6: 60,
          cdf_7: 60,
          cdf_8: 60,
          cdf_9: 60,
          cdf_10: 60,
          cdf_11: 60,
          LAST_TRAN_SERIAL_NUMBER: 150,
        };

        maxLength = fallbackLengths[lookupFieldName] || 0;
      }

      if (maxLength > 0 && row.searchTerm.trim().length > maxLength) {
        row.hasErrorSearchTerm = true;
        row.msgSearchTerm = `${fieldLabel} is invalid.`;
        return true;
      }

      return false;
    },
    validateNumericFields(row, fieldName, fieldLabel) {
      if (fieldName === 'STATE_STUDENT_ID') {
        const stateStudentIdRegex = /^[\d-]+$/;

        if (!stateStudentIdRegex.test(row.searchTerm.trim())) {
          row.hasErrorSearchTerm = true;
          row.msgSearchTerm = `${fieldLabel} is invalid.`;
          return true;
        }
      } else if (['SCHOOL_YEAR', 'LAST_TRAN_SERIAL_NUMBER', 'cdf_10'].includes(fieldName)) {
        const numericRegex = /^\d+$/;

        if (!numericRegex.test(row.searchTerm.trim())) {
          row.hasErrorSearchTerm = true;
          row.msgSearchTerm = `${fieldLabel} is invalid.`;
          return true;
        }
      }
      return false;
    },
    validateNameFields(row, fieldName, fieldLabel) {
      if (['FIRST_NAME', 'LAST_NAME', 'MIDDLE_NAME', 'PREVIOUS_LAST_NAME', 'SUFFIX'].includes(fieldName)) {
        const allowedCharactersRegex = new RegExp(`^${this.allowedCharacters}+$`);
        if (!allowedCharactersRegex.test(row.searchTerm.trim())) {
          row.hasErrorSearchTerm = true;
          row.msgSearchTerm = `${fieldLabel}  is invalid.`;
          return true;
        }
      }
      return false;
    },
    validateAlphanumericFields(row, fieldName, fieldLabel) {
      if (['LOCAL_STUDENT_ID', 'ALIAS_ID', 'cdf_11'].includes(fieldName)) {
        const alphanumericRegex = /^[a-zA-Z0-9-_ ]+$/;
        if (!alphanumericRegex.test(row.searchTerm.trim())) {
          row.hasErrorSearchTerm = true;
          row.msgSearchTerm = `${fieldLabel}  is invalid.`;
          return true;
        }
      }
      return false;
    },
    validateSSNField(row, fieldName, fieldLabel) {
      if (fieldName === 'SOCIAL_SECURITY_NUMBER') {
        const regexSSN = /^(?!000|666|124)[0-9]{3}([-]?)(?!00)[0-9]{2}([-]?)(?!0000)[0-9]{4}$/;
        const regexAlphanumeric = /^[a-zA-Z0-9]+$/;
        if (row.comparisonOperator[0].value === '=') {
          if (!regexSSN.test(row.searchTerm.trim())) {
            row.hasErrorSearchTerm = true;
            row.msgSearchTerm = `${fieldLabel}  is invalid.`;
            return true;
          }
        } else if (!regexSSN.test(row.searchTerm.trim())) {
          if (!regexAlphanumeric.test(row.searchTerm.trim())) {
            row.hasErrorSearchTerm = true;
            row.msgSearchTerm = `${fieldLabel}  is invalid.`;
            return true;
          }
        }
      }
      return false;
    },
    validateSchoolYearField(row, fieldName, fieldLabel) {
      if (fieldName === 'SCHOOL_YEAR') {
        if (row.comparisonOperator[0].value === '=') {
          if (!/^\d{4}$/.test(row.searchTerm.trim())) {
            row.hasErrorSearchTerm = true;
            row.msgSearchTerm = `${fieldLabel}  is invalid.`;
            return true;
          }
        }
      }
      return false;
    },
    validateDateField(row, fieldName, fieldLabel) {
      if (fieldName === 'DATE_OF_BIRTH' || fieldName === 'cdf_9') {
        if (!this.isValidDateFormat(row.searchTerm, 'YYYY-MM-DD')) {
          row.hasErrorSearchTerm = true;
          row.hasErrorSearchTerm = true;
          row.msgSearchTerm = `${fieldLabel}  is invalid.`;
          return true;
        }
      }
      return false;
    },
    validateDateFSegmentFields(row) {
      const numericRegex = /^\d+$/;

      const isValidDay = day => numericRegex.test(day) && parseInt(day, 10) >= 1
        && parseInt(day, 10) <= 31 && day.length <= 2;
      const isValidMonth = month => numericRegex.test(month) && parseInt(month, 10) >= 1
        && parseInt(month, 10) <= 12 && month.length <= 2;
      const isValidYear = year => numericRegex.test(year) && year.length === 4;

      let hasError = false;

      if (row.birthDay && !isValidDay(row.birthDay)) {
        row.hasErrorBirthDay = true;
        row.msgBirthDay = 'Birth Day is invalid';
        hasError = true;
      } else {
        row.hasErrorBirthDay = false;
        row.msgBirthDay = '';
      }

      if (row.birthMonth && !isValidMonth(row.birthMonth)) {
        row.hasErrorBirthMonth = true;
        row.msgBirthMonth = 'Birth Month is invalid';
        hasError = true;
      } else {
        row.hasErrorBirthMonth = false;
        row.msgBirthMonth = '';
      }

      if (row.birthYear && !isValidYear(row.birthYear)) {
        row.hasErrorBirthYear = true;
        row.msgBirthYear = 'Birth Year is invalid';
        hasError = true;
      } else {
        row.hasErrorBirthYear = false;
        row.msgBirthYear = '';
      }

      if (!row.birthDay && !row.birthMonth && !row.birthYear) {
        row.hasErrorSearchTerm = true;
        row.msgSearchTerm = 'Search Term is required';
        hasError = true;
      } else {
        row.hasErrorSearchTerm = false;
        row.msgSearchTerm = '';
      }

      return hasError;
    },
    checkRequiredFields(row) {
      row.hasErrorFieldName = !row.fieldName || row.fieldName.length === 0;
      row.msgFieldName = row.hasErrorFieldName ? 'Field name is required' : '';
      row.hasErrorOperator = !row.operator || row.operator.length === 0;
      row.msgOperator = row.hasErrorOperator ? 'Operator is required' : '';
      row.hasErrorComparisonOperator = !row.comparisonOperator
        || row.comparisonOperator.length === 0;
      row.msgComparisonOperator = row.hasErrorComparisonOperator ? 'Comparison operator is required' : '';
      row.hasErrorSearchTerm = !row.searchTerm || row.searchTerm.length === 0;
      row.msgSearchTerm = row.hasErrorSearchTerm ? 'Search term is required' : '';
    },
    async search({ saveState = true, pageNum = null } = {}) {
      const isValid = this.validateFields();
      if (!isValid) { return; }

      const queryParams = {
        personTypeCodeList: this.personTypes !== null
          ? this.personTypes.map(item => item.code) : undefined,
        searchIn: this.selectedSearchInOption,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        sqlQuery: encodeURIComponent(`${this.sqlQuery.substring(0, 20)}...${Date.now()}`),
        page: pageNum || undefined,
      };

      this.$router.push({
        name: 'PidSearchAdvanced',
        query: queryParams,
      });

      if (saveState) {
        this.$store.dispatch('personSearchModule/setSearchState', {
          searchType: 'searchByAdvanced',
          selectedSearchInOption: this.selectedSearchInOption,
          personType: this.personType,
          personTypes: this.personTypes,
          rows: this.rows,
          showQuery: this.showQuery,
        });
      }

      this.searchParams();
      this.$emit('show', false);
    },

    convertToColumnNames(sqlQueryWithCodes) {
      const columnNameMapping = {
        FIRST_NAME: 'firstName',
        LAST_NAME: 'lastName',
        MIDDLE_NAME: 'middleName',
        GENDER_CODE: 'genderCode',
        SUFFIX: 'suffix',
        SOCIAL_SECURITY_NUMBER: 'socialSecurityNumber',
        DATE_OF_BIRTH: 'dateOfBirth',
        LOCAL_STUDENT_ID: 'localId',
        STATE_STUDENT_ID: 'stateStudentId',
        SCHOOL_YEAR: 'schoolYear',
        PREVIOUS_LAST_NAME: 'previousLastName',
        SOURCE_CODE: 'sourceCode',
        ALIAS_ID: 'aliasId',
        DISTRICT_CODE: 'districtCode',
        SCHOOL_CODE: 'schoolCode',
        BIRTHDAY: 'birthDay',
        BIRTHMONTH: 'birthMonth',
        BIRTHYEAR: 'birthYear',
        LAST_TRAN_SERIAL_NUMBER: 'pidSubmissionId ',
      };

      let sqlQueryWithCamelCase = sqlQueryWithCodes;

      Object.entries(columnNameMapping).forEach(([code, columnName]) => {
        const regex = new RegExp(`\\b${code}\\b`, 'g');
        sqlQueryWithCamelCase = sqlQueryWithCamelCase.replace(regex, columnName);
      });

      return sqlQueryWithCamelCase.trim();
    },
    searchParams() {
      const params = {};

      params.personTypes = this.personTypes !== null
        ? this.personTypes.map(item => item.code) : undefined;
      params.selectedSearchInOption = this.selectedSearchInOption;
      params.sqlQuery = this.convertToColumnNames(this.sqlQueryWithCodes);
      this.$emit('searchParams', params);
      return params;
    },
    addNewParameter() {
      this.rows.push({
        fieldName: '',
        operator: this.operators.length > 0 ? [this.operators[0]] : [],
        comparisonOperator: '',
        searchTerm: '',
        birthMonth: '',
        birthDay: '',
        birthYear: '',
        hasErrorFieldName: false,
        hasErrorOperator: false,
        hasErrorComparisonOperator: false,
        hasErrorSearchTerm: false,
        hasErrorBirthMonth: false,
        hasErrorBirthDay: false,
        hasErrorBirthYear: false,
        msgFieldName: '',
        msgOperator: '',
        msgComparisonOperator: '',
        msgSearchTerm: '',
        msgbirthMonth: '',
        msgBirthDay: '',
        msgBirthYear: '',
      });
    },
    generateSQL() {
      if (this.rows.length === 0 || (this.rows.length === 1 && !this.rows[0].fieldName)) {
        this.sqlQuery = 'No Parameters have been added.';
        return;
      }

      let sqlQueryWithLabels = '';
      let sqlQueryWithCodes = '';

      this.rows.forEach((row, index) => {
        const operator = row.operator ? row.operator[0].value : 'AND';
        const fieldNameLabel = row.fieldName ? row.fieldName[0].label : '';
        const fieldNameCode = row.fieldName ? row.fieldName[0].value : '';
        const comparisonOperatorLabel = row.comparisonOperator ? row.comparisonOperator[0].label : '';

        const searchTerm = row.searchTerm ? row.searchTerm.trim() : '';

        if (index > 0) {
          sqlQueryWithLabels += ` ${operator} `;
          sqlQueryWithCodes += ` ${operator} `;
        }

        if (fieldNameCode === 'DATE_OF_BIRTH_SEGMENTS') {
          const conditionsLabels = [];
          const conditionsCodes = [];

          if (row.birthDay) {
            const birthDayConditionLabel = this.constructCondition('Birth Day', row.birthDay.padStart(2, '0'), comparisonOperatorLabel);
            const birthDayConditionCode = this.constructCondition('birthDay', row.birthDay.padStart(2, '0'), comparisonOperatorLabel);
            conditionsLabels.push(birthDayConditionLabel);
            conditionsCodes.push(birthDayConditionCode);
          }
          if (row.birthMonth) {
            const birthMonthConditionLabel = this.constructCondition('Birth Month', row.birthMonth.padStart(2, '0'), comparisonOperatorLabel);
            const birthMonthConditionCode = this.constructCondition('birthMonth', row.birthMonth.padStart(2, '0'), comparisonOperatorLabel);
            conditionsLabels.push(birthMonthConditionLabel);
            conditionsCodes.push(birthMonthConditionCode);
          }
          if (row.birthYear) {
            const birthYearConditionLabel = this.constructCondition('Birth Year', row.birthYear, comparisonOperatorLabel);
            const birthYearConditionCode = this.constructCondition('birthYear', row.birthYear, comparisonOperatorLabel);
            conditionsLabels.push(birthYearConditionLabel);
            conditionsCodes.push(birthYearConditionCode);
          }
          if (conditionsLabels.length > 0) {
            sqlQueryWithLabels += conditionsLabels.join(' AND ');
          }
          if (conditionsCodes.length > 0) {
            sqlQueryWithCodes += conditionsCodes.join(' AND ');
          }
        } else {
          sqlQueryWithLabels += this.constructCondition(
            fieldNameLabel, searchTerm, comparisonOperatorLabel,
          );
          sqlQueryWithCodes += this.constructCondition(
            fieldNameCode, searchTerm, comparisonOperatorLabel,
          );
        }
      });

      if (this.rows.length > 0) {
        sqlQueryWithLabels = `(${sqlQueryWithLabels})`;
        sqlQueryWithCodes = `(${sqlQueryWithCodes})`;
      }

      this.sqlQuery = sqlQueryWithLabels;
      this.sqlQueryWithCodes = sqlQueryWithCodes;
      this.convertToColumnNames(this.sqlQueryWithCodes);
    },
    constructCondition(fieldName, value, comparisonOperatorLabel) {
      switch (comparisonOperatorLabel) {
        case 'contains':
          return `${fieldName} LIKE '%${value}%'`;
        case 'starts with':
          return `${fieldName} LIKE '${value}%'`;
        case 'ends with':
          return `${fieldName} LIKE '%${value}'`;
        case 'is (exact)':
          return `${fieldName} = '${value}'`;
        default:
          return `${fieldName} = '${value}'`;
      }
    },
    toggleDisplay(event) {
      event.preventDefault();
      this.showQuery = !this.showQuery;
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    reset() {
      if (this.$refs.resetPersonTypes) { this.$refs.resetPersonTypes.clear(); }
      this.rows = [];
      this.sqlQuery = null;
      this.showQuery = false;
      this.hasErrorSchoolYear = false;
      this.msgSchoolYear = null;
      this.resetNotifications();
      this.selectedSearchInOption = 'Master';
      this.$store.dispatch('personSearchModule/resetSearchState', 'searchByAdvanced');
      this.$emit('show', true);
      this.$router.replace({ query: {} }).catch(() => {});
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    setDataElements() {
      this.searchInOptions = [];
      if (this.hasModule('PRSID:SRCH:OPTIONS:ADV:SRCH')) {
        this.searchInOptions.push({ label: 'Master' });
      }
      if (this.hasModule('PRSID:SRCH:OPTIONS:HIST:SRCH')) {
        this.searchInOptions.push({ label: 'History' });
      }
      if (this.hasModule('PRSID:SRCH:OPTIONS:INPUTREC:SRCH')) {
        this.searchInOptions.push({ label: 'Submission' });
      }
      this.allPersonType = [
        {
          code: null,
          name: 'All',
        },
      ];
      this.personType = this.defaultPersonType;
    },
  },
};
</script>

<style lang="sass" scoped>
  a.disabled
    color: #757575
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .border--hidden
    border-bottom: hidden
  .row-warnings
    height: auto
    line-height: 0.5
  .fullwidth
    width: 100%
  .half-width
    width: 50%
  .date-input
    width: 25%
  .one-fourth-width
    width: 25%
  .separator
    margin: 0 5px
  .text-area--margin-right
    margin-right: 3.25rem
  .invalid-feedback
    font-style: italic
    color: #dc3545
    font-size: .7rem
    padding: .25rem 0 .25rem .15rem
  .birthdate-container
    display: flex
    align-items: flex-start
  .date-segment
    display: flex
    flex-direction: column
    align-items: center
    margin-right: 0
  .separator
    display: flex;
    align-items: center;
  .date-input
    width: 150px
  .table
    margin: 0
    width: 100%
    table-layout: fixed
    border-collapse: collapse
    th, td
      overflow-wrap: break-word
  .table-wrapper
    padding-left: 2rem
  .editable-cell
    padding: 0.5rem !important
  .sr-only
    position: absolute
    width: 1px
    height: 1px
    padding: 0
    margin: -1px
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap
    border: 0
  </style>
